import React, { useCallback, useRef, useState } from "react";
import LeadBillingPromptModal, { LeadBillingAction } from "../components/common/LeadBillingPromptModal";
import ConvertLeadDialog, { ConvertLeadOutcome } from "../components/crm/ConvertLeadDialog";
import { getMemberById } from "../api/membershipAPI";
import { getConvertPreview, LeadConvertPreview } from "../api/leadsAPI";
import { safeGetItem } from "@mediatechgroup/app-version";

/**
 * The ONE "this family is still a lead" prompt for every staff screen that
 * creates an invoice or takes a payment for an account.
 *
 * Dan, 2026-10-09, on staff invoicing a lead and taking a payment while the
 * family stays a lead: "there should be something that prompts conversion if
 * invoice creation is attempted for a lead - don't block."
 *
 *   const { confirmLeadBilling, leadBillingPrompt } = useLeadBillingPrompt();
 *
 *   const decision = await confirmLeadBilling({ membershipId, accountType, action: "invoice" });
 *   if (!decision.proceed) return;                    // staff went back; nothing was written
 *   await createInvoice(withLeadBillingChoice(payload, decision), token);
 *   ...
 *   return <>{leadBillingPrompt}...</>;
 *
 * - Resolves `{ proceed: true }` WITHOUT prompting for a member or a
 *   customer, so callers await it unconditionally.
 * - For a lead it shows the prompt and resolves when staff choose:
 *     "Keep as a lead and continue"       -> proceed, choice "kept_as_lead"
 *     "Convert to member, then continue"  -> the existing Convert dialog runs
 *                                            in place; once converted,
 *                                            proceed, choice "converted_first"
 *     "Go back"                           -> proceed false
 *   The calling form stays mounted the whole time, so what staff typed is
 *   untouched, and the caller's own submit simply carries on.
 * - Never blocks: a failed lookup reads as "not a lead".
 * - Once per attempt: ask ONCE at the top of a submit and reuse the decision
 *   for every write in it. A screen opened as the next step of the same
 *   attempt (the card form after Create & Pay) is handed the decision as
 *   `decided` and does not ask again. An enroll screen that already showed
 *   the "enrolling makes this family a member" warning (useLeadEnrollConfirm)
 *   passes `enrollmentConfirmed` and is not asked a second question.
 *
 * There is no "make them a customer" choice: nothing in the app turns a lead
 * into a customer (the only supported changes are lead -> member and
 * customer -> member), so offering it here would be a new, second path.
 *
 * The choice travels with the write as `lead_billing_choice`
 * (withLeadBillingChoice) and the backend writes it to the activity log with
 * the staff member's name (middleware/leadBillingNotice).
 */
export type LeadBillingChoice = "kept_as_lead" | "converted_first" | "converting_by_enrollment";

export interface LeadBillingDecision {
  /** false: staff went back. Write nothing. */
  proceed: boolean;
  /** Set when the account was a lead and staff chose. Send it with the write. */
  choice?: LeadBillingChoice;
}

export interface LeadBillingRequest {
  /** The account the invoice or payment is for. */
  membershipId?: string | null;
  /** memberships.account_type when the caller already has it. */
  accountType?: string | null;
  /** Shown in the sentence, e.g. "The Rivera family". */
  familyName?: string | null;
  /** What staff are about to do. */
  action: LeadBillingAction;
  /** A decision an earlier step of the SAME attempt already collected. */
  decided?: LeadBillingDecision | null;
  /** The enroll warning was already shown and accepted for this submit. */
  enrollmentConfirmed?: boolean;
}

const PROCEED: LeadBillingDecision = { proceed: true };

// Accounts this browser session has seen converted, or seen to be a member or
// customer already. A screen's own copy of the account can be stale (it was
// loaded as a lead); these stop a second prompt for a family staff just
// converted, and save a lookup for one already known not to be a lead.
const convertedHere = new Set<string>();
const knownNotLead = new Set<string>();

/** Test hook: forget what this session has seen. */
export function resetLeadBillingMemory(): void {
  convertedHere.clear();
  knownNotLead.clear();
}

/**
 * Adds the staff choice to a request body. Leaves the body exactly as it was
 * when no choice was made (a member, a customer), so those requests are
 * unchanged.
 */
export function withLeadBillingChoice<T extends object>(
  payload: T,
  decision: LeadBillingDecision | null | undefined,
): T {
  if (!decision?.choice) return payload;
  return { ...payload, lead_billing_choice: decision.choice };
}

/**
 * The server's own word that the account is a lead (`account_is_lead` on the
 * answer to an invoice or payment write). For a screen that could not ask
 * beforehand because it does not hold the account (a registration row with no
 * account id on it): tell staff afterward. Says nothing when staff were asked.
 */
export function leadBilledWithoutAsking(
  answer: { account_is_lead?: boolean } | null | undefined,
  decision: LeadBillingDecision | null | undefined,
): boolean {
  return answer?.account_is_lead === true && !decision?.choice;
}

export const LEAD_BILLED_AFTERWARD_MESSAGE =
  "Saved. This family is still a lead, not a member yet. To make them a member, open their page and choose Convert to Member.";

/** The account a camp, clinic or event registration row belongs to, however the list names it. */
export function registrationMembershipId(registration: unknown): string | null {
  const r = (registration || {}) as {
    membership_id?: string | null;
    membership?: { id?: string | null } | null;
    memberships?: { id?: string | null } | null;
  };
  return r.membership_id || r.membership?.id || r.memberships?.id || null;
}

interface UseLeadBillingPromptOptions {
  /** Called after staff converted the lead from the prompt, so the screen can refresh its copy. */
  onConverted?: (membershipId: string) => void;
}

export function useLeadBillingPrompt(options: UseLeadBillingPromptOptions = {}) {
  const [open, setOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [request, setRequest] = useState<LeadBillingRequest>({ action: "invoice" });
  const [preview, setPreview] = useState<LeadConvertPreview | null>(null);
  const [previewState, setPreviewState] = useState<"loading" | "ready" | "error">("loading");
  const resolverRef = useRef<((value: LeadBillingDecision) => void) | null>(null);
  const onConvertedRef = useRef(options.onConverted);
  onConvertedRef.current = options.onConverted;

  const confirmLeadBilling = useCallback(
    async (req: LeadBillingRequest): Promise<LeadBillingDecision> => {
      // An earlier step of this attempt already asked.
      if (req.decided) return req.decided;
      // The enroll warning already told staff this makes the family a member:
      // no second question. The record says what they confirmed.
      if (req.enrollmentConfirmed) {
        return req.accountType === "lead"
          ? { proceed: true, choice: "converting_by_enrollment" }
          : PROCEED;
      }

      const membershipId = req.membershipId || null;
      if (membershipId && (convertedHere.has(membershipId) || knownNotLead.has(membershipId))) {
        return PROCEED;
      }

      let accountType = req.accountType;
      let familyName = req.familyName;
      const token = safeGetItem("authToken");

      // The caller did not know the account type: ask the server rather than assume.
      if (accountType == null && membershipId) {
        try {
          const res: any = await getMemberById(token, membershipId);
          accountType = res?.data?.account_type ?? null;
          if (!familyName && res?.data) {
            familyName = `${res.data.first_name || ""} ${res.data.last_name || ""}`.trim() || null;
          }
        } catch {
          // A failed lookup must never block an invoice or a payment.
          accountType = null;
        }
      }

      if (accountType !== "lead" || !membershipId) {
        if (membershipId && accountType) knownNotLead.add(membershipId);
        return PROCEED;
      }

      // A prompt still open from an earlier click counts as "went back".
      if (resolverRef.current) {
        resolverRef.current({ proceed: false });
        resolverRef.current = null;
      }

      setRequest({ ...req, familyName });
      setPreview(null);
      setPreviewState("loading");
      setConverting(false);
      setOpen(true);

      // What converting would bill: the Convert dialog's own preview.
      getConvertPreview(token || "", membershipId)
        .then((res) => {
          if (res?.success && res.data) {
            setPreview(res.data);
            setPreviewState("ready");
          } else {
            setPreviewState("error");
          }
        })
        .catch(() => setPreviewState("error"));

      return new Promise<LeadBillingDecision>((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [],
  );

  const settle = useCallback((decision: LeadBillingDecision) => {
    setOpen(false);
    setConverting(false);
    if (resolverRef.current) {
      resolverRef.current(decision);
      resolverRef.current = null;
    }
  }, []);

  const handleConverted = useCallback(
    (_outcome: ConvertLeadOutcome) => {
      const id = request.membershipId;
      if (id) {
        convertedHere.add(id);
        onConvertedRef.current?.(id);
      }
      settle({ proceed: true, choice: "converted_first" });
    },
    [request.membershipId, settle],
  );

  const leadBillingPrompt = (
    <>
      {/* Mounted only while it is asking, so a screen that never bills a
          lead renders nothing extra. */}
      {open && !converting && (
        <LeadBillingPromptModal
          isOpen
          familyName={request.familyName}
          action={request.action}
          preview={preview}
          previewState={previewState}
          onGoBack={() => settle({ proceed: false })}
          onKeepAsLead={() => settle({ proceed: true, choice: "kept_as_lead" })}
          onConvertFirst={() => setConverting(true)}
        />
      )}
      {open && converting && request.membershipId && (
        <ConvertLeadDialog
          token={safeGetItem("authToken") || ""}
          leadId={request.membershipId}
          leadName={request.familyName?.trim() || "this family"}
          // The account staff are billing must still exist afterward, so no
          // "merge into another family" here; the dialog says where to do that.
          allowMerge={false}
          // Cancel returns to the three choices, not to the form.
          onClose={() => setConverting(false)}
          onConverted={handleConverted}
        />
      )}
    </>
  );

  return { confirmLeadBilling, leadBillingPrompt };
}
